import React, { useState } from 'react';
import { IconButton, Typography } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { Helmet } from 'react-helmet';
import {
    APP_CLOUDINARY_CLOUDNAME,
    APP_CLOUDINARY_UPLOAD_PRESET,
    APP_CLOUDINARY_URL_ENDPOINT,
} from '../../../../../constants/api_urls';
import { generateCroppedUrl, removeCropParameters } from '../../../../google-pmax-campaign/googlePmaxFormComponent/Utils/imageTools/imageCropper';
import { validateImage } from '../../Utils/imageValidators';
import { CLOUDINARY_IMAGE_CATEGORIES, DEFAULT_RATIOS } from '../../../../google-pmax-campaign/constants/cloudinaryImageCategory';
import "./MediaLibrary.scss"

const SearchAdsMediaLibrary = ({
    landscape,
    setLandscape,
    square,
    setSquare,
    portrait,
    setPortrait,
    logo,
    setLogo,
    landscapeLogo,
    setLandscapeLogo,
    errors = {},
}) => {
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const [fullScreenImage, setFullScreenImage] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    const getCategoryArray = (category) => {
        switch (category) {
            case 'landscape': return landscape;
            case 'square': return square;
            case 'portrait': return portrait;
            case 'logo': return logo;
            case 'landscapeLogo': return landscapeLogo;
            default: return null;
        }
    };

    const setCategoryArray = (category, updatedArray) => {
        switch (category) {
            case 'landscape': setLandscape(updatedArray); break;
            case 'square': setSquare(updatedArray); break;
            case 'portrait': setPortrait(updatedArray); break;
            case 'logo': setLogo(updatedArray); break;
            case 'landscapeLogo': setLandscapeLogo(updatedArray); break;
            default: break;
        }
    };

    const openUploadWidget = (category) => {
        const defaultRatio = DEFAULT_RATIOS[category];
        if (!defaultRatio) return;

        window.cloudinary.openUploadWidget(
            {
                cloudName: APP_CLOUDINARY_CLOUDNAME,
                uploadPreset: APP_CLOUDINARY_UPLOAD_PRESET,
                multiple: true,
                sources: ['local', 'url'],
                cropping: false,
                croppingAspectRatio: defaultRatio,
                showSkipCropButton: false,
                transformation: [
                    {
                        aspect_ratio: `${Math.round(defaultRatio * 100)}:${100}`,
                        crop: 'fill',
                    },
                ],
            },
            async (error, result) => {
                if (!error && result?.event === 'success') {
                    const croppedUrl = generateCroppedUrl(result.info);
                    await Promise.all([
                        (async () => {
                            setCategoryArray(category, [...(getCategoryArray(category) || []), croppedUrl]);
                        })(),
                        (async () => {
                            const validationResult = await validateImage(category, result.info.secure_url);
                            setValidationErrors((prev) => ({
                                ...prev,
                                [croppedUrl]: validationResult.success ? null : validationResult.error,
                            }));
                        })(),
                    ]);
                }
            }
        );
    };

    const handleDelete = (index, category) => {
        const arr = getCategoryArray(category);
        if (!arr) return;
        setCategoryArray(category, arr.filter((_, i) => i !== index));
    };

    const formatCategoryKey = (key) =>
        key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^[a-z]/, (c) => c.toUpperCase());

    const renderCategory = (categoryKey) => {
        const categoryArray = getCategoryArray(categoryKey);
        if (!categoryArray) return null;

        return categoryArray.map((url, index) => {
            const categoryError = errors[`${categoryKey}Image`];
            const errorMessage =
                validationErrors[url] ||
                (typeof categoryError === 'string'
                    ? categoryError
                    : categoryError?.[`index-${index}`]) ||
                null;

            const isHovered = hoveredIndex === index && hoveredCategory === categoryKey;

            return (
                <div
                    className={`image-card ${errorMessage != null ? 'error' : ''}`}
                    key={index}
                    onMouseEnter={() => { setHoveredIndex(index); setHoveredCategory(categoryKey); }}
                    onMouseLeave={() => { setHoveredIndex(null); setHoveredCategory(null); }}
                >
                    <img src={url} alt="Uploaded" />
                    {(isHovered || errorMessage != null) && (
                        <div className={`image-overlay ${errorMessage != null ? 'has-error' : ''}`}>
                            {errorMessage && <p className="error-message">{errorMessage}</p>}
                            <div className="image-actions">
                                <IconButton color="primary" onClick={() => setFullScreenImage(url)}>
                                    <VisibilityIcon />
                                </IconButton>
                                <IconButton
                                    color="secondary"
                                    className="delete-icon"
                                    onClick={() => handleDelete(index, categoryKey)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </div>
                        </div>
                    )}
                </div>
            );
        });
    };

    return (
        <div className="cloudinary-image-editor">
            <Helmet>
                <script src={APP_CLOUDINARY_URL_ENDPOINT} type="text/javascript" />
            </Helmet>

            <div className="image-grid">
                {CLOUDINARY_IMAGE_CATEGORIES.map((category) => (
                    <div className="cloudinary-category" key={category}>
                        <div className="category-header">
                            <h4>
                                <div className="category-header-title">
                                    {formatCategoryKey(category)}
                                    {typeof errors[`${category}Image`] === 'string' && (
                                        <Typography className="text-error-section">
                                            {errors[`${category}Image`]}
                                        </Typography>
                                    )}
                                </div>
                                <AddCircleOutlineIcon
                                    className="add-icon"
                                    onClick={() => openUploadWidget(category)}
                                />
                            </h4>
                        </div>
                        <div className="image-category">{renderCategory(category)}</div>
                    </div>
                ))}
            </div>

            {fullScreenImage && (
                <div className="full-screen-overlay" onClick={() => setFullScreenImage(null)}>
                    <img
                        src={removeCropParameters(fullScreenImage)}
                        alt="Full Screen"
                        className="full-screen-image"
                    />
                </div>
            )}
        </div>
    );
};

export default SearchAdsMediaLibrary;
